import config from '../config/environment';

class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 5000;
    this.messageHandlers = new Map();
    this.connectionHandlers = [];
    this.disconnectionHandlers = [];
    this.isConnecting = false;
  }

  connect() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return;
    }

    if (this.isConnecting) {
      return;
    }

    this.isConnecting = true;
    const wsUrl = this.getWebSocketUrl();
    
    try {
      this.ws = new WebSocket(wsUrl);
      this.setupEventListeners();
    } catch (error) {
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  getWebSocketUrl() {
    return config.WS_URL;
  }

  setupEventListeners() {
    if (!this.ws) return;

    this.ws.onopen = () => {
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      
      // Send ping to establish connection
      this.send('ping', { timestamp: new Date().toISOString() });
      
      // Notify connection handlers
      this.connectionHandlers.forEach(handler => {
        try {
          handler();
        } catch (error) {
          // Silent error handling
        }
      });
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        this.handleMessage(message);
      } catch (error) {
        // Silent error handling
      }
    };

    this.ws.onclose = (event) => {
      this.isConnecting = false;
      
      // Notify disconnection handlers
      this.disconnectionHandlers.forEach(handler => {
        try {
          handler();
        } catch (error) {
          // Silent error handling
        }
      });

      // Attempt to reconnect if not a clean close
      if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (error) => {
      this.isConnecting = false;
    };
  }

  handleMessage(message) {
    const { type, data } = message;
    
    if (type === 'pong') {
      // Handle ping/pong for connection health
      return;
    }

    // Call registered handlers for this message type
    const handlers = this.messageHandlers.get(type) || [];
    handlers.forEach(handler => {
      try {
        handler(data || message);
      } catch (error) {
        // Silent error handling
      }
    });
  }

  send(type, data = {}) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const message = { type, ...data };
      this.ws.send(JSON.stringify(message));
      return true;
    }
    return false;
  }

  onMessage(type, handler) {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, []);
    }
    this.messageHandlers.get(type).push(handler);

    // Return unsubscribe function
    return () => {
      const handlers = this.messageHandlers.get(type);
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      }
    };
  }

  onConnect(handler) {
    this.connectionHandlers.push(handler);
    
    // If already connected, call handler immediately
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      handler();
    }

    // Return unsubscribe function
    return () => {
      const index = this.connectionHandlers.indexOf(handler);
      if (index > -1) {
        this.connectionHandlers.splice(index, 1);
      }
    };
  }

  onDisconnect(handler) {
    this.disconnectionHandlers.push(handler);

    // Return unsubscribe function
    return () => {
      const index = this.disconnectionHandlers.indexOf(handler);
      if (index > -1) {
        this.disconnectionHandlers.splice(index, 1);
      }
    };
  }

  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1);
    
    setTimeout(() => {
      this.connect();
    }, delay);
  }

  disconnect() {
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent reconnection
  }

  getConnectionState() {
    if (!this.ws) return 'disconnected';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'disconnected';
      default:
        return 'unknown';
    }
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  // Utility methods for common message types
  subscribeToSystemMetrics(handler) {
    return this.onMessage('system_stats', handler);
  }

  subscribeToAlerts(handler) {
    return this.onMessage('system_alert', handler);
  }

  subscribeToLogs(handler) {
    return this.onMessage('log_message', handler);
  }

  requestSystemStats() {
    return this.send('request_system_stats');
  }

  requestMetricHistory(metricType, timeRange = '1h') {
    return this.send('request_metric_history', { metricType, timeRange });
  }
}

// Create singleton instance
export const websocketService = new WebSocketService();

export default websocketService;
